import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import AnalyticsService from 'cio/services/analytics';
import ProductActivationController from '../controller';
import AccountService from 'cio/services/account';
import { ProductApp, productActivationInfo } from 'cio/utils/product-app-info';

const Validations = {
  platform(this: ExperienceController, value: string) {
    if (!('platformOptions' in this.product)) {
      return true;
    }
    if (!value?.trim()) {
      return 'Please select a platform';
    }

    return true;
  },

  platformOther: {
    dependentKeys: ['platform'],
    validator(this: ExperienceController, value: string) {
      if (this.activateController.getFormValues('platform') !== 'other') {
        return true;
      }

      if (!value?.trim()) {
        return 'Please input a platform';
      }

      return true;
    },
  },
  employeeCount(this: ExperienceController, value: string) {
    if (!('employeeCount' in this.product.formValues)) {
      return true;
    }
    if (!value?.trim()) {
      return 'Please select the number of employees in your company';
    }
    return true;
  },
};

export default class ExperienceController extends Controller {
  validations!: FormValidations;

  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @controller('product-activation') activateController!: ProductActivationController;

  messagingPlatforms = productActivationInfo[ProductApp.JOURNEYS].platformOptions;
  cdpPlatforms = productActivationInfo[ProductApp.DATA_PIPELINES].platformOptions;

  get product() {
    return this.activateController.product;
  }

  constructor() {
    super(...arguments);
    this.set(
      'validations',
      FormValidations.create({ validations: Validations, context: this, modelKey: 'activateController.formValues' }),
    );
  }

  submitTask = task(async (ev: MouseEvent) => {
    ev.preventDefault();
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    try {
      this.activateController.activationStepCompleted('experience');
      this.activateController.goToNextStep();
    } catch (e) {
      // The parent task shows an error message, so we don't need any error handling here.
      return;
    }
  });

  get isSubmitDisabled() {
    return this.validations.hasErrors || this.submitTask.isRunning;
  }

  @action
  onValueChange(field: keyof ProductActivationController['formValues'], value: string) {
    this.activateController.setFormValue(field, value);
    this.validations.validateAttribute(field);
  }

  get question() {
    if ('platformOptions' in this.product) {
      return this.product.platformQuestion;
    }
    return this.product.employeeCountQuestion;
  }

  get options() {
    if ('platformOptions' in this.product) {
      return this.product.platformOptions;
    }
    return this.product.employeeCountOptions;
  }

  get formValues() {
    if ('platformOptions' in this.product) {
      return this.activateController.formValues.platform;
    }
    return this.activateController.formValues.employeeCount;
  }

  get formValuesOther() {
    return this.activateController.formValues.platformOther;
  }

  get formValidations() {
    if ('platformOptions' in this.product) {
      return this.validations.errors.platform;
    }
    return this.validations.errors.employeeCount;
  }

  get formValidationsOther() {
    return this.validations.errors.platformOther;
  }

  get field() {
    if ('platformOptions' in this.product) {
      return 'platform';
    }
    return 'employeeCount';
  }
}

declare module '@ember/controller' {
  interface Registry {
    'product-activation.experience': ExperienceController;
  }
}
